import { createContext, useContext, useEffect, useRef, useState } from 'react';
import platformApi from '../services/platformApi';
import tokenStorage from './tokenStorage';
import { PLATFORM_PUBLIC_PATHS } from '../config/platformPublicPaths';

const PlatformAuthContext = createContext();

const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

export const PlatformAuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => tokenStorage.getUser());
  const [cargando, setCargando] = useState(true);
  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleTokenRefresh = (expiresInSeconds) => {
    clearRefreshTimer();
    const delayMs = Math.max((expiresInSeconds - 60) * 1000, 0);
    refreshTimerRef.current = setTimeout(() => refreshAccessToken(true), delayMs);
  };

  const scheduleRefreshFromStorage = () => {
    const expiresAt = tokenStorage.getTokenExpiresAt();
    if (expiresAt > Date.now()) {
      scheduleTokenRefresh(Math.ceil((expiresAt - Date.now()) / 1000));
    }
  };

  const refreshAccessToken = async (forceRefresh = false) => {
    const lastActivity = tokenStorage.getLastActivity() || Date.now();
    if (!forceRefresh && Date.now() - lastActivity >= INACTIVITY_TIMEOUT) {
      await logout();
      return false;
    }

    try {
      const { data } = await platformApi.post('/auth/renovar-token');
      if (!data.token) return false;

      tokenStorage.setAccessToken(data.token, data.expiresIn);
      tokenStorage.touchActivity();
      if (data.expiresIn) {
        scheduleTokenRefresh(data.expiresIn);
      }
      return true;
    } catch {
      await logout();
      return false;
    }
  };

  const silentRefreshIfNeeded = async () => {
    if (tokenStorage.getAccessToken()) {
      return true;
    }

    try {
      const { data } = await platformApi.post('/auth/renovar-token');
      if (data.token) {
        tokenStorage.setAccessToken(data.token, data.expiresIn);
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  const logout = async () => {
    try {
      await platformApi.post('/auth/logout');
    } catch {
      // ignore
    } finally {
      clearRefreshTimer();
      tokenStorage.clear();
      setUser(null);
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const init = async () => {
      if (PLATFORM_PUBLIC_PATHS.includes(window.location.pathname)) {
        setCargando(false);
        return;
      }

      const hasSession = await silentRefreshIfNeeded();
      if (!hasSession) {
        tokenStorage.clear();
        setUser(null);
        window.location.href = '/login';
        return;
      }

      try {
        const { data } = await platformApi.get('/auth/perfil');
        setUser(data.user);
        tokenStorage.setSession({
          token: tokenStorage.getAccessToken(),
          user: data.user,
        });
        scheduleRefreshFromStorage();
      } catch {
        const refreshed = await refreshAccessToken(true);
        if (refreshed) {
          try {
            const { data } = await platformApi.get('/auth/perfil');
            setUser(data.user);
            tokenStorage.setSession({
              token: tokenStorage.getAccessToken(),
              user: data.user,
            });
          } catch {
            tokenStorage.clear();
            setUser(null);
            window.location.href = '/login';
          }
        }
      } finally {
        setCargando(false);
      }
    };

    init();

    return () => clearRefreshTimer();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await platformApi.post('/auth/login', { email, password });
      if (!data.token || !data.user) {
        return { success: false, message: 'Respuesta de login inválida' };
      }

      tokenStorage.setSession({
        token: data.token,
        user: data.user,
        expiresIn: data.expiresIn,
      });
      setUser(data.user);
      if (data.expiresIn) {
        scheduleTokenRefresh(data.expiresIn);
      }
      return { success: true };
    } catch (error) {
      const payload = error.response?.data || {};
      const message =
        payload.errors?.email?.[0] ||
        payload.errors?.auth?.[0] ||
        payload.message ||
        'Credenciales inválidas';
      return { success: false, message };
    }
  };

  return (
    <PlatformAuthContext.Provider
      value={{
        user,
        cargando,
        login,
        logout,
        estaAutenticado: tokenStorage.isAuthenticated() && !!user,
      }}
    >
      {!cargando && children}
    </PlatformAuthContext.Provider>
  );
};

export const usePlatformAuth = () => {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth debe usarse dentro de PlatformAuthProvider');
  return ctx;
};
